/**
 * Staff who never got in (#120, 2026-10-14). The hotel added 24 staff and none of them ever logged in:
 * nobody was invited, and the manager typed every clocked shift by hand. Ansatte shows per person
 * whether they are invited, logged in and on the phone, sends the welcome to everyone not yet invited,
 * and shows one person's welcome link as a QR code for somebody with no address. «Appen på papir» is
 * a sheet with the store and web addresses as QR codes to print or show.
 */
import { ApiError, post } from '@/api/client';
import { type ApiUser, type InviteResult, useUserWrites } from '@/api/people';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { Button, Drawer, toast } from '@/ui';
import { fmtDs, fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Status } from '@/ui/page';
import { useQueryClient } from '@tanstack/react-query';
import qrcode from 'qrcode-generator';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { accessedHere } from './tasks';

/** the phone app in the stores, as backend/services/appVersion.js STORE_URLS names them */
export const STORE_URLS = {
  ios: 'https://apps.apple.com/app/id6670785637',
  android: 'https://play.google.com/store/apps/details?id=com.timefly.scheduler',
};

const DAY_MS = 86_400_000;
type Who = Pick<
  ApiUser,
  'id' | 'email' | 'active_status' | 'last_login_at' | 'accessed_at' | 'invited_at' | 'invite'
>;

/**
 * Logged in here; has an account from another company but no session here yet (PROM-28); the newest
 * welcome mailed, shown as a link, or invited before the way was recorded; its mail failed; its link ran
 * out unused; or none (ONB-11). The date is when, as an Oslo day.
 */
export type InviteKind = 'in' | 'elsewhere' | 'mail' | 'link' | 'invited' | 'expired' | 'failed' | 'none';
export function invitation(u: Who): { kind: InviteKind; date: string | null } {
  const day = (at: string) => oslo(at)?.date ?? null;
  const i = u.invite;
  const here = accessedHere(u);
  if (here) return { kind: 'in', date: day(here) };
  if (u.last_login_at) return { kind: 'elsewhere', date: null };
  if (i?.via === 'failed') return { kind: 'failed', date: day(i.at) };
  if (!u.invited_at) return { kind: 'none', date: null };
  if (i?.expired_at) return { kind: 'expired', date: day(i.expired_at) };
  return { kind: i?.via ?? 'invited', date: day(u.invited_at) };
}

/** The state in words with its day, as the card and the Ansatte row show it. */
export function inviteText(k: InviteKind, d: string | null): string {
  const dd = d ? fmtDs(d) : '';
  if (k === 'in') return T('Logget inn {d}', { d: dd });
  if (k === 'elsewhere') return T('Har konto, ikke logget inn her ennå');
  if (k === 'mail') return T('Invitasjon sendt på e-post {d}', { d: dd });
  if (k === 'link') return T('Invitasjonslenke vist {d}', { d: dd });
  if (k === 'invited') return T('Invitert {d}', { d: dd });
  if (k === 'expired') return T('Lenken utløp {d}. Send en ny.', { d: dd });
  if (k === 'failed') return T('E-posten kunne ikke sendes {d}. Send på nytt.', { d: dd });
  return T('Ikke invitert');
}

/**
 * Whom «Send til alle» mails, the server's rule (POST /admin/invitations): active, never logged in, an
 * address, not the person pressing it; never invited, or with `again` invited more than a day ago.
 */
export function bulkTargets(people: Who[], meId: number | undefined, again: boolean, now = Date.now()) {
  return people.filter(
    (u) =>
      u.active_status &&
      !u.last_login_at &&
      !!u.email &&
      u.id !== meId &&
      (again ? !!u.invited_at && now - new Date(u.invited_at).getTime() > DAY_MS : !u.invited_at),
  );
}

/** The chip in the Innlogging column, and whether the phone app is registered. */
export function InviteState({ u }: { u: Who & Pick<ApiUser, 'has_app'> }) {
  const s = invitation(u);
  return (
    <>
      {s.kind === 'in' ? (
        // logged in is the normal state: plain text, no green chip (#272 B-12)
        <span className="ans-mute">{inviteText(s.kind, s.date)}</span>
      ) : (
        <Status tone={['none', 'failed', 'expired'].includes(s.kind) ? 'warn' : 'act'}>
          {inviteText(s.kind, s.date)}
        </Status>
      )}
      {u.has_app ? (
        <small>{T('Bruker appen')}</small>
      ) : (
        s.kind === 'in' && <small>{T('Ikke appen ennå')}</small>
      )}
    </>
  );
}

/** A QR code as an SVG of dark squares, error correction M: readable from a phone held to a screen or paper. */
export function Qr({ text, label, size = 168 }: { text: string; label: string; size?: number }) {
  const q = qrcode(0, 'M');
  q.addData(text);
  q.make();
  const n = q.getModuleCount();
  let d = '';
  for (let r = 0; r < n; r++)
    for (let c = 0; c < n; c++) if (q.isDark(r, c)) d += `M${c + 4} ${r + 4}h1v1h-1z`;
  return (
    <svg
      viewBox={`0 0 ${n + 8} ${n + 8}`}
      width={size}
      height={size}
      role="img"
      aria-label={label}
      data-testid="qr"
      shapeRendering="crispEdges"
    >
      <rect width="100%" height="100%" fill="#fff" />
      <path d={d} fill="#000" />
    </svg>
  );
}

/** A sheet on <body> while printing only, the way ui/print.tsx prints the plan. */
function PrintOnly({ children }: { children: React.ReactNode }) {
  return createPortal(<div className="printsheet invsheet">{children}</div>, document.body);
}

/**
 * One person's welcome link, made when the drawer opens (POST /admin/users/:id/welcome-link): the QR
 * code to scan from the manager's screen, the link to copy into a message, and the username. Making it
 * ends any earlier link, mailed or shown.
 */
export function LinkDrawer({
  u,
  onClose,
}: { u: Pick<ApiUser, 'id' | 'name' | 'lastname'> | null; onClose: () => void }) {
  const [got, setGot] = useState<{ link: string; username: string } | { error: string } | null>(null);
  const qc = useQueryClient();
  const id = u?.id;
  useEffect(() => {
    setGot(null);
    if (id == null) return;
    post<{ link: string; username: string }>(`/admin/users/${id}/welcome-link`, {})
      .then((r) => {
        setGot(r);
        void qc.invalidateQueries({ queryKey: ['users'] }); // it counts as an invitation
      })
      .catch((e: Error) => setGot({ error: e.message }));
  }, [id, qc]);
  const name = u ? [u.name, u.lastname].filter(Boolean).join(' ') : '';
  const ok = got && 'link' in got ? got : null;
  return (
    <Drawer
      open={u != null}
      onClose={onClose}
      title={T('Invitasjon til {name}', { name })}
      sub={T(
        'La personen skanne koden, eller send lenken i en melding. Den gjelder i 7 dager og kan brukes én gang.',
      )}
      footer={
        ok && (
          <>
            <Button
              onClick={() => {
                void navigator.clipboard?.writeText(ok.link).then(
                  () => toast(T('Lenken er kopiert')),
                  () => toast(T('Kunne ikke kopiere. Merk lenken og kopier den selv.')),
                );
              }}
            >
              {T('Kopier lenken')}
            </Button>
            <Button onClick={() => window.print()}>{T('Skriv ut')}</Button>
          </>
        )
      }
    >
      {got == null && <p>{T('Lager lenken …')}</p>}
      {got && 'error' in got && <div className="al dgr">{got.error}</div>}
      {ok && (
        <div className="inv-link" data-testid="invite-link">
          <Qr text={ok.link} label={T('QR-kode for invitasjonen til {name}', { name })} />
          <p>
            {T('Brukernavn')}: <b>{ok.username}</b>
          </p>
          <p className="inv-url">{ok.link}</p>
          <p className="sub">{T('En lenke sendt tidligere slutter å virke nå.')}</p>
          <PrintOnly>
            <h1>{T('Velkommen til Timefly, {name}', { name })}</h1>
            <Qr text={ok.link} label={T('QR-kode for invitasjonen til {name}', { name })} size={260} />
            <p>{T('Skann koden og velg et passord. Brukernavnet ditt er {u}.', { u: ok.username })}</p>
            <p>{T('Koden gjelder i 7 dager og kan brukes én gang.')}</p>
          </PrintOnly>
        </div>
      )}
    </Drawer>
  );
}

/** «Appen på papir»: the App Store, Google Play and the web login as QR codes, to print or show. */
export function AppSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const web = `${window.location.origin}/login`;
  const codes = [
    { k: 'ios', title: T('iPhone: App Store'), url: STORE_URLS.ios },
    { k: 'android', title: T('Android: Google Play'), url: STORE_URLS.android },
    { k: 'web', title: T('På nett'), url: web },
  ];
  const grid = (size: number) => (
    <div className="inv-codes">
      {codes.map((c) => (
        <figure key={c.k}>
          <Qr text={c.url} label={c.title} size={size} />
          <figcaption>
            <b>{c.title}</b>
            <span>{c.url}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={T('Appen på papir')}
      sub={T(
        'Heng opp arket eller vis det på skjermen. Brukernavn og passord får hver ansatt i invitasjonen.',
      )}
      footer={<Button onClick={() => window.print()}>{T('Skriv ut')}</Button>}
    >
      {open && grid(140)}
      {open && (
        <PrintOnly>
          <h1>{T('Timefly: vaktplan og timer på mobilen')}</h1>
          {grid(200)}
          <p>
            {T(
              'Logg inn med brukernavnet og passordet fra invitasjonen. Spør lederen om du ikke har fått den.',
            )}
          </p>
        </PrintOnly>
      )}
    </Drawer>
  );
}

/**
 * «Se mottakere» (#270 EMP-05): the exact people an invitation or a reminder goes to, before anything
 * is sent. Each recipient shows the address it goes to, or that it has none and is left out (with
 * «Vis lenke» for them); the consequence is said once. Sending posts the reviewed ids
 * (POST /admin/invitations with user_ids) and shows each person's outcome; the retry names only the
 * failed ones, so nobody who got the mail gets it twice. Sent is «Invitasjon sendt», never logged in,
 * and an invitation changes no role and no employment. Opening it sends nothing.
 */
export function InviteReview({
  ids,
  people,
  meId,
  onClose,
  onRetry,
}: {
  ids: number[] | null;
  people: (ApiUser & { email?: string | null })[];
  meId: number | undefined;
  onClose: () => void;
  onRetry: (ids: number[]) => void;
}) {
  const w = useUserWrites();
  const [results, setResults] = useState<InviteResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linkFor, setLinkFor] = useState<ApiUser | null>(null);
  const key = ids?.join(',') ?? '';
  // a new review (or the retry's narrower one) starts without the last outcome
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset per reviewed set
  useEffect(() => {
    setResults(null);
    setError(null);
  }, [key]);
  const byId = new Map(people.map((p) => [p.id, p]));
  const list = (ids ?? []).map((id) => byId.get(id)).filter((p): p is ApiUser => p != null);
  // who would actually get a mail: active, never logged in, an address, not the person sending it
  const eligible = list.filter((p) => p.active_status && !p.last_login_at && p.id !== meId);
  const ready = eligible.filter((p) => !!p.email);
  const without = eligible.filter((p) => !p.email);
  const send = async () => {
    setError(null);
    try {
      const r = await w.invite.mutateAsync(ready.map((p) => p.id));
      setResults(r.results);
    } catch (e) {
      const err = e instanceof ApiError ? e : null;
      setError(
        err?.status === 503
          ? T('E-post er ikke satt opp her, så ingenting ble sendt.')
          : err?.status === 429
            ? T('Sendt for mange ganger. Prøv igjen om en time.')
            : err?.code === 'demo'
              ? T('En demobedrift sender ikke e-post. Ingenting ble sendt.')
              : err
                ? `${T('Ingenting ble sendt')}: ${err.message}`
                : // no answer: some may have gone; the list shows who is invited once it is read again
                  T(
                    'Svaret kom ikke fram. Noen kan ha fått e-posten. Lukk og se Tilgang i listen før du prøver igjen.',
                  ),
      );
    }
  };
  const failedIds = results?.filter((r) => r.outcome === 'failed').map((r) => r.id) ?? [];
  const outcome = (r: InviteResult) =>
    r.outcome === 'sent'
      ? r.kind === 'reminder'
        ? T('Påminnelse sendt')
        : T('Invitasjon sendt')
      : r.outcome === 'failed'
        ? T('Ble ikke sendt')
        : r.outcome === 'no_email'
          ? T('Mangler e-post, ikke sendt')
          : T('Ikke sendt: har logget inn eller er ikke aktiv');
  const sentN = results?.filter((r) => r.outcome === 'sent').length ?? 0;
  return (
    <>
      <Drawer
        open={ids != null && linkFor == null}
        onClose={onClose}
        blockClose={() => w.invite.isPending}
        title={
          results
            ? failedIds.length
              ? T('Noen invitasjoner ble ikke sendt')
              : T('Invitasjoner sendt')
            : T('Se mottakere før sending')
        }
        sub={
          results
            ? T('{n} av {m} sendt. Første innlogging er ikke registrert før personen logger inn.', {
                n: sentN,
                m: results.length,
              })
            : T(
                'Hver mottaker får en e-post med lenke til å velge passord. Lenken gjelder i 7 dager. Invitasjonen endrer ikke rolle eller arbeidsforhold.',
              )
        }
        footer={
          results ? (
            <>
              <span className="sp" />
              <Button onClick={onClose}>{T('Ferdig')}</Button>
              {failedIds.length > 0 && (
                <Button tone="pri" onClick={() => onRetry(failedIds)} data-testid="invite-retry">
                  {T('{n, plural, one {Prøv den som feilet på nytt} other {Prøv de # som feilet på nytt}}', {
                    n: failedIds.length,
                  })}
                </Button>
              )}
            </>
          ) : (
            <>
              <span className="sp" />
              <Button onClick={onClose} disabled={w.invite.isPending}>
                {T('Avbryt')}
              </Button>
              {ready.length > 0 && (
                <Button tone="pri" onClick={send} disabled={w.invite.isPending} data-testid="invite-send">
                  {w.invite.isPending ? T('Sender…') : T('Send til {n}', { n: ready.length })}
                </Button>
              )}
            </>
          )
        }
      >
        {error && (
          <div className="al dgr" role="alert">
            <Icon name="stop" />
            <span>{error}</span>
          </div>
        )}
        {!results && (
          <p className="inv-sum" data-testid="invite-summary">
            {ready.length
              ? T('{n, plural, one {# mottaker har} other {# mottakere har}} e-post.', { n: ready.length })
              : T('Ingen av disse har en e-postadresse å sende til.')}
            {without.length > 0 &&
              ` ${T('{n, plural, one {# person mangler} other {# personer mangler}} e-post og blir utelatt.', { n: without.length })}`}
          </p>
        )}
        <ul className="inv-list" data-testid="invite-recipients">
          {(results
            ? results.map((r) => byId.get(r.id)).filter((p): p is ApiUser => p != null)
            : eligible
          ).map((p) => {
            const r = results?.find((x) => x.id === p.id);
            return (
              <li key={p.id} className={r ? `inv-r ${r.outcome}` : undefined}>
                <b>{fullName(p)}</b>
                <small>{p.email || T('Mangler e-post, får ikke invitasjon')}</small>
                {r ? (
                  <small className="inv-out">{outcome(r)}</small>
                ) : (
                  // no address: the line above already says it gets nothing, so no «Ny invitasjon» under it
                  p.email && (
                    <small>
                      {p.invited_at
                        ? T('Påminnelse om invitasjonen fra {d}', {
                            d: fmtDs(oslo(p.invited_at)?.date ?? ''),
                          })
                        : T('Ny invitasjon')}
                    </small>
                  )
                )}
                {!p.email && !r && (
                  <button type="button" className="mlink" onClick={() => setLinkFor(p)}>
                    {T('Vis lenke i stedet')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {!results && list.length > eligible.length && (
          <p className="hint">
            {T('{n} i utvalget har logget inn, er ikke aktive eller er deg selv, og får ingen e-post.', {
              n: list.length - eligible.length,
            })}
          </p>
        )}
      </Drawer>
      <LinkDrawer u={linkFor} onClose={() => setLinkFor(null)} />
    </>
  );
}
